/**
 * ThemeManager
 * 
 * Utility for applying themes via CSS variables.
 * This is a pure function that manipulates the DOM directly,
 * ensuring zero React re-renders when themes change.
 */

import { ThemeId, THEMES, DEFAULT_THEME } from "./tokens";

/**
 * Applies a theme by setting CSS variables on :root
 * 
 * @param themeId - The theme identifier to apply
 * @returns void
 * 
 * This function is idempotent and fast. It directly manipulates
 * CSS variables without triggering React re-renders.
 */
export function applyTheme(themeId: ThemeId): void {
  // Validate themeId and fallback to default if invalid
  const validThemeId: ThemeId = THEMES[themeId] ? themeId : DEFAULT_THEME;
  const theme = THEMES[validThemeId];
  
  // Apply all theme tokens as CSS variables on :root
  const root = document.documentElement;
  
  Object.entries(theme).forEach(([key, value]) => {
    root.style.setProperty(key, value);
  });
  
  // Store current theme in a data attribute for debugging/inspection
  root.setAttribute("data-theme", validThemeId);
}

/**
 * Gets the currently applied theme from the DOM
 * 
 * @returns ThemeId or null if not set
 */
export function getCurrentTheme(): ThemeId | null {
  const themeId = document.documentElement.getAttribute("data-theme") as ThemeId | null;
  return themeId && THEMES[themeId] ? themeId : null;
}

/**
 * Initializes theme system with default theme
 * Call this once on app startup before rendering
 */
export function initializeTheme(defaultThemeId?: ThemeId): void {
  const themeId = defaultThemeId || DEFAULT_THEME;
  applyTheme(themeId);
}
